import { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Eye, Pencil, ToggleLeft, ToggleRight, Lock, Unlock, BookOpen, Trash2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { userService, roleService, courseService, masterService } from '../../services/api';
import { Button, Modal, Badge, Skeleton, EmptyState, Toast } from '../../components/ui';
import { thStyle, tdStyle, iconBtnStyle } from '../../utils/tableStyles';
import CreateUserForm from './CreateUserForm';
import EditUserForm from './EditUserForm';

const UserManagement = () => {
  const { dark } = useTheme();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('users', 'create');
  const canEdit = hasPermission('users', 'update');
  const canDelete = hasPermission('users', 'delete');

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState(null);

  const [roles, setRoles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [retailOUs, setRetailOUs] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [coursesUser, setCoursesUser] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [deleteUserId, setDeleteUserId] = useState(null);

  const PAGE_SIZE = 20;

  useEffect(() => {
    roleService.getAll().then(({ data }) => setRoles(data.data || [])).catch(() => {});
    courseService.getAll({ limit: 200 }).then(({ data }) => setCourses(data.data || [])).catch(() => {});
    masterService.getRetailOUs().then(({ data }) => setRetailOUs(data.data || [])).catch(() => {});
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await userService.getAll({ page, limit: PAGE_SIZE, search: search || undefined, role: filterRole || undefined });
      setUsers(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setToast({ msg: 'Error loading users', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleToggleStatus = async (id) => {
    try {
      await userService.toggleStatus(id);
      fetchUsers();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error', type: 'error' });
    }
  };

  const handleToggleLock = async (id) => {
    try {
      await userService.toggleLock(id);
      fetchUsers();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error', type: 'error' });
    }
  };

  const openCourses = (u) => {
    setCoursesUser(u);
    setCourseList(u.assignedCourses?.map(c => (c._id || c)) || []);
  };

  const handleSaveCourses = async () => {
    try {
      await userService.updateCourses(coursesUser._id, courseList);
      setToast({ msg: 'Courses updated', type: 'success' });
      setCoursesUser(null);
      fetchUsers();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error updating courses', type: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await userService.delete(deleteUserId);
      setToast({ msg: 'User deleted', type: 'success' });
      setDeleteUserId(null);
      fetchUsers();
    } catch (err) {
      setToast({ msg: err.response?.data?.message || 'Error deleting user', type: 'error' });
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const card = { background: dark ? '#0F172A' : '#fff', border: `1px solid ${dark ? '#1E293B' : '#E2E8F0'}`, borderRadius: 16, overflow: 'hidden' };

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: dark ? '#F1F5F9' : '#0F172A', margin: 0 }}>Users</h1>
          <p style={{ fontSize: 13, color: dark ? '#64748B' : '#94A3B8', margin: '4px 0 0' }}>{total} total users</p>
        </div>
        {canCreate && <Button icon={Plus} onClick={() => setCreateOpen(true)}>Add User</Button>}
      </div>

      <div style={card}>
        <div style={{ display: 'flex', gap: 10, padding: '14px 20px', borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}`, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} color={dark ? '#475569' : '#94A3B8'} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search by name, email..." style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <select value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }} style={{ padding: '7px 10px', borderRadius: 8, fontSize: 13, border: `1px solid ${dark ? '#334155' : '#E2E8F0'}`, background: dark ? '#1E293B' : '#F8FAFC', color: dark ? '#F1F5F9' : '#0F172A', outline: 'none' }}>
            <option value="">All Roles</option>
            {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
          </select>
        </div>

        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 20 }}><Skeleton rows={6} cols={5} /></div>
          ) : users.length === 0 ? (
            <div style={{ padding: 40 }}><EmptyState title="No users found" /></div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: dark ? '#0F172A' : '#F8FAFC' }}>
                <tr>
                  <th style={thStyle(dark)}>Name</th>
                  <th style={thStyle(dark)}>Email</th>
                  <th style={thStyle(dark)}>Role</th>
                  <th style={thStyle(dark)}>Retail OU</th>
                  <th style={thStyle(dark)}>Status</th>
                  <th style={thStyle(dark)}>Perf. Pts</th>
                  <th style={thStyle(dark)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} style={{ borderBottom: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={tdStyle(dark)}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{u.firstName} {u.lastName || ''}</div>
                      <div style={{ fontSize: 11, color: dark ? '#64748B' : '#94A3B8' }}>{u.employeeId || ''}</div>
                    </td>
                    <td style={tdStyle(dark)}>{u.email}</td>
                    <td style={tdStyle(dark)}>
                      <Badge color={u.role?.color || '#6366F1'}>{u.role?.name || '—'}</Badge>
                    </td>
                    <td style={tdStyle(dark)}>{u.retailOU?.name || '—'}</td>
                    <td style={tdStyle(dark)}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Badge color={u.status === 'active' ? '#10B981' : '#EF4444'}>{u.status}</Badge>
                        {u.isLocked && <Badge color="#F59E0B">Locked</Badge>}
                      </div>
                    </td>
                    <td style={{ ...tdStyle(dark), fontWeight: 700, color: '#F59E0B' }}>{u.performancePoints ?? '—'}</td>
                    <td style={tdStyle(dark)}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {canEdit && (
                          <button title="Edit" style={iconBtnStyle(dark)} onClick={() => setEditUser(u)}><Pencil size={14} /></button>
                        )}
                        <button title={u.status === 'active' ? 'Deactivate' : 'Activate'} style={iconBtnStyle(dark)} onClick={() => handleToggleStatus(u._id)}>
                          {u.status === 'active' ? <ToggleRight size={14} color="#10B981" /> : <ToggleLeft size={14} />}
                        </button>
                        <button title={u.isLocked ? 'Unlock' : 'Lock'} style={iconBtnStyle(dark)} onClick={() => handleToggleLock(u._id)}>
                          {u.isLocked ? <Unlock size={14} color="#F59E0B" /> : <Lock size={14} />}
                        </button>
                        {u.role?.code === 'COUNSELLOR' && (
                          <button title="Manage Courses" style={iconBtnStyle(dark)} onClick={() => openCourses(u)}><BookOpen size={14} /></button>
                        )}
                        {canDelete && (
                          <button title="Delete" style={{ ...iconBtnStyle(dark), color: '#EF4444', borderColor: '#FCA5A5' }} onClick={() => setDeleteUserId(u._id)}><Trash2 size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: `1px solid ${dark ? '#1E293B' : '#F1F5F9'}` }}>
            <span style={{ fontSize: 12, color: dark ? '#64748B' : '#94A3B8' }}>Page {page} of {totalPages}</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Create User" size="lg">
        <CreateUserForm roles={roles} retailOUs={retailOUs} courses={courses} onClose={() => setCreateOpen(false)} onCreated={fetchUsers} />
      </Modal>

      <Modal open={!!editUser} onClose={() => setEditUser(null)} title="Edit User" size="lg">
        {editUser && <EditUserForm user={editUser} roles={roles} retailOUs={retailOUs} onClose={() => setEditUser(null)} onUpdated={fetchUsers} />}
      </Modal>

      <Modal open={!!coursesUser} onClose={() => setCoursesUser(null)} title="Manage Counsellor Courses" size="md">
        {coursesUser && (
          <div>
            <p style={{ fontSize: 13, color: dark ? '#94A3B8' : '#64748B', margin: '0 0 16px' }}>
              Select courses that <strong>{coursesUser.firstName}</strong> specializes in:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, maxHeight: 320, overflowY: 'auto' }}>
              {courses.map(c => (
                <label key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${courseList.includes(c._id) ? '#3B82F6' : (dark ? '#334155' : '#E2E8F0')}`, background: courseList.includes(c._id) ? (dark ? '#1E3A5F' : '#EFF6FF') : 'transparent', cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={courseList.includes(c._id)} onChange={() => setCourseList(prev => prev.includes(c._id) ? prev.filter(x => x !== c._id) : [...prev, c._id])} />
                  {c.name}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="outline" onClick={() => setCoursesUser(null)}>Cancel</Button>
              <Button onClick={handleSaveCourses}>Save Courses</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!deleteUserId} onClose={() => setDeleteUserId(null)} title="Confirm Delete" size="sm">
        <p style={{ fontSize: 14, color: dark ? '#CBD5E1' : '#334155', margin: '0 0 20px' }}>Are you sure you want to delete this user?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setDeleteUserId(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagement;
